import { HRM_PERMISSIONS } from '@/constant/hr/permissions';
import { useHasPermission } from '@/hooks/use-has-permission';
import { getUserRoles } from '@/hooks/use-user-roles';
import { useEmployeesByUserIds } from '@/modules/shared/hr-lookups';
import { useGlobalQuery } from '@/state/query-client/hooks';
import { useAuthStore } from '@/state/store/auth';
import { getPerformanceDashboard } from '../services/dashboard.service';

export const useGetPerformanceDashboard = (employeeProfileId?: string, enabled = true) =>
  useGlobalQuery({
    queryKey: ['hr-performance-dashboard', employeeProfileId],
    queryFn: () => getPerformanceDashboard(employeeProfileId as string),
    enabled: Boolean(employeeProfileId) && enabled,
  });

/** Head of Business / Technology / Customer Success (legacy IsHob / IsHot / IsHocs). */
const HEAD_ROLES = ['hob', 'hot', 'hocs'];

export interface DashboardAccess {
  /** Whose data the current user may open: everyone (admin), their own unit (heads), or nobody. */
  scope: 'all' | 'unit' | 'none';
  /** Unit heads are limited to this unit. */
  departmentId?: string;
  canView: boolean;
  /** A head's unit (or the picked employee's) is still loading: the access isn't known yet. */
  isResolving: boolean;
}

/**
 * Legacy DashboardService.CanViewUserData: nobody opens their own dashboard, admins open anyone's, and a head of
 * business / technology / customer success opens the dashboards of people in their own unit.
 */
export const useDashboardAccess = (selectedEmployeeId: string): DashboardAccess => {
  const user = useAuthStore((state) => state.user);
  const currentUserId = user?.itemId ?? '';
  const roles = getUserRoles(user ? { memberships: user.memberships ?? [] } : null);
  const isAdmin = useHasPermission(HRM_PERMISSIONS.PERFORMANCE.DASHBOARD_VIEW_ALL);
  const isHead = roles.some((role) => HEAD_ROLES.includes(role));

  const { byUserId, isLoading } = useEmployeesByUserIds([currentUserId, selectedEmployeeId]);
  const departmentId = byUserId.get(currentUserId)?.DepartmentId;
  const selectedDepartmentId = byUserId.get(selectedEmployeeId)?.DepartmentId;

  const scope: DashboardAccess['scope'] = isAdmin
    ? 'all'
    : isHead && departmentId
      ? 'unit'
      : 'none';
  const isSelf = Boolean(selectedEmployeeId) && selectedEmployeeId === currentUserId;
  const canView =
    Boolean(selectedEmployeeId) &&
    !isSelf &&
    (scope === 'all' ||
      (scope === 'unit' && Boolean(selectedDepartmentId) && selectedDepartmentId === departmentId));

  // Admins don't need the units; a head does, so until they load "not authorized" would be a guess.
  const isResolving = !isAdmin && isHead && isLoading;

  return { scope, departmentId, canView, isResolving };
};
